import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "../api/api";
import { getApiBaseUrl } from "../api/configHelper";

const normalizeAttachmentUrl = (filePath, baseUrl) => {
  if (!filePath) return "";

  const normalizedPath = String(filePath).replace(/\\/g, "/");
  if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath;

  const uploadIndex = normalizedPath.toLowerCase().indexOf("/uploadfiles/");
  if (uploadIndex >= 0) {
    return `${baseUrl}${normalizedPath.slice(uploadIndex)}`;
  }

  return normalizedPath;
};

let itemMasterAttachmentMapPromise = null;

const getItemMasterAttachmentMap = () => {
  if (!itemMasterAttachmentMapPromise) {
    itemMasterAttachmentMapPromise = Promise.all([
      api.GetAttachments("Item_Master"),
      getApiBaseUrl()
    ])
      .then(([response, baseUrl]) => {
        const attachments = response?.Attachments?.Table || [];
        return attachments.reduce((map, attachment) => {
          if (attachment?.PrimeId && attachment?.FilePath) {
            const primeId = String(attachment.PrimeId);
            const urls = map.get(primeId) || [];
            urls.push(normalizeAttachmentUrl(attachment.FilePath, baseUrl));
            map.set(primeId, urls);
          }
          return map;
        }, new Map());
      })
      .catch((error) => {
        itemMasterAttachmentMapPromise = null;
        throw error;
      });
  }

  return itemMasterAttachmentMapPromise;
};

export default function MenuItemCard({ item, dateStr, isOrderAllowed, rainRestrictionMessage = "" }) {
  const { cart, addToCart, findMenuItem, isTakeaway, user } = useApp();

  const [animating, setAnimating] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartXRef = useRef(null);
  const suppressCardClickRef = useRef(false);

  const id = String(item.ItemId ?? item.Item_No ?? item.ItemNo);
  const name = item.Item_Desc1 || item.name || "Unnamed Item";
  const foundItem = findMenuItem(id);
  
  let regularPrice;
  if (foundItem && foundItem.regularPrice !== undefined && !user?.selectedAdditionalAddress) {
    // Use regularPrice from foundItem (this is the non-takeaway price)
    regularPrice = foundItem.regularPrice;
  } else if (user?.selectedAdditionalAddress && user?.division === 'Circle1') {
    regularPrice = Number(item.Circle1Rate ?? item.MRP ?? item.price ?? 0) || 0;
  } else if (user?.selectedAdditionalAddress && user?.division === 'Circle2') {
    regularPrice = Number(item.Circle2Rate ?? item.MRP ?? item.price ?? 0) || 0;
  } else if (user?.selectedAdditionalAddress && user?.division === 'Circle3') {
    regularPrice = Number(item.Circle3Rate ?? item.MRP ?? item.price ?? 0) || 0;
  } else if (user?.UserType === 'New User') {
    // New customers always get Circle3 rate
    regularPrice = Number(item.Circle3Rate ?? item.MRP ?? item.price ?? 0) || 0;
  } else if (user?.division === 'Circle1') {
    regularPrice = Number(item.Circle1Rate ?? item.MRP ?? item.price ?? 0) || 0;
  } else if (user?.division === 'Circle2') {
    regularPrice = Number(item.Circle2Rate ?? item.MRP ?? item.price ?? 0) || 0;
  } else if (user?.division === 'Circle3') {
    regularPrice = Number(item.Circle3Rate ?? item.MRP ?? item.price ?? 0) || 0;
  } else {
    // Fallback to Circle3Rate for users without division
    regularPrice = Number(item.Circle3Rate ?? item.MRP ?? item.price ?? 0) || 0;
  }
  
  // Get takeaway price from foundItem or item
  const takeawayPrice = foundItem?.takeawayPrice ?? (item.TakeAway !== undefined ? Number(item.TakeAway) : regularPrice);
  const price = isTakeaway && takeawayPrice !== undefined ? takeawayPrice : regularPrice;

  const isAddButtonDisabled = () => {
    if (price <= 0) return true;

    // If PinAllowed is "NotAvail"
    if (user?.PinAllowed === "NotAvail") {
      // Only allow "Other" item type
      if (item.Item_Type === "Other") {
        return !isOrderAllowed;
      }

      return true;
    }
    if (rainRestrictionMessage) return true;
    return !isOrderAllowed;
  };
  
  console.log('MenuItemCard: itemId =', id, 'userType =', user?.UserType, 'division =', user?.division, 'isTakeaway =', isTakeaway, 'price =', price, 'takeawayPrice =', takeawayPrice, 'foundItem =', foundItem);
  // Fetch image from API
  useEffect(() => {
    const fetchImage = async () => {
      try {
        const attachmentMap = await getItemMasterAttachmentMap();
        const matchedImageUrls = attachmentMap.get(String(item.ItemId)) || [];

        if (matchedImageUrls.length > 0) {
          setImageUrls(matchedImageUrls);
          setCurrentImageIndex(0);
        } else {
          setImageUrls([]);
          setCurrentImageIndex(0);
        }
      } catch (error) {
        console.error("Error fetching image:", error);
        setImageUrls([]);
        setCurrentImageIndex(0);
      }
    };

    if (id) {
      fetchImage();
    }
  }, [id, item.ItemId, item.Item_No]);

  const qty = cart?.[dateStr]?.[id] || 0;
  const hasImages = imageUrls.length > 0;
  const hasMultipleImages = imageUrls.length > 1;
  const currentImageUrl = hasImages ? imageUrls[currentImageIndex] : "";

  const handleAdd = (delta) => {
    setAnimating(true);
    addToCart(dateStr, id, delta, item);
    setTimeout(() => setAnimating(false), 300);
  };

  const showPreviousImage = (event) => {
    event?.stopPropagation();
    setCurrentImageIndex((index) =>
      imageUrls.length ? (index - 1 + imageUrls.length) % imageUrls.length : 0
    );
  };

  const showNextImage = (event) => {
    event?.stopPropagation();
    setCurrentImageIndex((index) =>
      imageUrls.length ? (index + 1) % imageUrls.length : 0
    );
  };

  const handleImageTouchStart = (event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const handleImageTouchEnd = (event) => {
    if (!hasMultipleImages || touchStartXRef.current === null) return;

    const endX = event.changedTouches?.[0]?.clientX;
    const deltaX = endX - touchStartXRef.current;
    touchStartXRef.current = null;

    if (Math.abs(deltaX) < 35) return;

    suppressCardClickRef.current = true;
    if (deltaX > 0) {
      showPreviousImage(event);
    } else {
      showNextImage(event);
    }
  };

  return (
    <motion.div
      whileHover={{ scale: isAddButtonDisabled() ? 1 : 1.02, y: isAddButtonDisabled() ? 0 : -2 }}
      whileTap={{ scale: isAddButtonDisabled() ? 1 : 0.98 }}
      onClick={() => {
        if (suppressCardClickRef.current) {
          suppressCardClickRef.current = false;
          return;
        }

        if (!isAddButtonDisabled() && isOrderAllowed) handleAdd(1);
      }}
      className={`relative flex flex-col w-full h-full max-w-sm p-3 overflow-hidden transition-all duration-300 bg-gradient-to-br from-gray-100 to-gray-200 shadow-md rounded-xl group ${
        isAddButtonDisabled() ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-lg'
      }`}
    >
      <div className="absolute inset-0 transition-opacity duration-300 opacity-0 bg-gradient-to-br from-red-50/30 to-red-50/20 group-hover:opacity-100 rounded-xl"></div>

      <div
        className="relative w-full aspect-square max-w-[250px] mx-auto mb-5 rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 shadow-sm group-hover:shadow-md transition-shadow duration-300"
        onTouchStart={handleImageTouchStart}
        onTouchEnd={handleImageTouchEnd}
      >
        {currentImageUrl ? (
          <img
            src={currentImageUrl}
            alt={name}
            className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
            onError={() => {
              setImageUrls((urls) => urls.filter((url) => url !== currentImageUrl));
              setCurrentImageIndex(0);
            }}
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full px-4 text-sm font-black leading-tight text-center text-red-800 uppercase bg-orange-50">
            {name}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent"></div>

        {hasMultipleImages && (
          <>
            <button
              type="button"
              aria-label="Previous image"
              onClick={showPreviousImage}
              className="absolute left-2 top-1/2 z-20 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-gray-700 shadow-md transition hover:bg-white"
            >
              <ChevronLeft size={17} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              aria-label="Next image"
              onClick={showNextImage}
              className="absolute right-2 top-1/2 z-20 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-gray-700 shadow-md transition hover:bg-white"
            >
              <ChevronRight size={17} strokeWidth={2.5} />
            </button>
            <div className="absolute bottom-2 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/35 px-2 py-1">
              {imageUrls.map((_, index) => (
                <button
                  type="button"
                  key={`${id}-image-dot-${index}`}
                  aria-label={`View image ${index + 1}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  className={`h-1.5 rounded-full transition-all ${
                    index === currentImageIndex ? "w-4 bg-white" : "w-1.5 bg-white/60"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="relative z-10 flex flex-col justify-between flex-1 px-2 text-center">
        <div className="flex flex-col items-center justify-center mb-2">
          <motion.h3
            className="pb-1 text-sm font-black leading-tight text-center text-gray-800 break-words transition-colors duration-300 border-b-2 border-gray-300 sm:text-base group-hover:text-gray-900"
            whileHover={{ scale: 1.02 }}
          >
            {item.Item_Desc1 || item.name || "Unnamed Item"}
          </motion.h3>

          {/* Show Item_Desc2 below Item_Desc1 */}
          {item.Item_Desc2 && (
            <p className="text-[11px] sm:text-xs font-medium text-gray-600 mt-2 leading-tight">
              {item.Item_Desc2}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between mb-3">
          <motion.div
            className="flex items-center gap-1"
            whileHover={{ scale: 1.05 }}
          >
            <span className="text-lg font-black text-transparent bg-gradient-to-r from-red-600 to-red-600 bg-clip-text">
              ₹{price.toFixed(2)}
            </span>
          </motion.div>

          {qty > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              whileHover={{ scale: 1.1 }}
              className="bg-gradient-to-r from-red-500 to-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[24px] text-center shadow-md border border-red-400/30"
            >
              {qty}
            </motion.span>
          )}
        </div>
      </div>

      <div className="relative z-10">
        {qty > 0 ? (
          <motion.div
            key="controls"
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 25
            }}
            className="flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-md"
          >
            <motion.button
              whileHover={{
                scale: 1.1,
                backgroundColor: "#64748b"
              }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => { e.stopPropagation(); handleAdd(-1); }}
              disabled={isAddButtonDisabled()}
              className="flex items-center justify-center w-8 h-8 text-sm font-bold text-white transition-all duration-200 rounded-full shadow-md bg-slate-500 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              −
            </motion.button>

            <motion.span
              key={qty}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 20
              }}
              className="font-bold text-gray-800 text-sm min-w-[20px] text-center"
            >
              {qty}
            </motion.span>

            <motion.button
              whileHover={{
                scale: 1.1,
                backgroundColor: "#0891b2"
              }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => { e.stopPropagation(); handleAdd(1); }}
              disabled={isAddButtonDisabled()}
              className="flex items-center justify-center w-8 h-8 text-sm font-bold text-white transition-all duration-200 rounded-full shadow-md bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              +
            </motion.button>
          </motion.div>
        ) : (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={(e) => { e.stopPropagation(); handleAdd(1); }}
            disabled={isAddButtonDisabled()}
            className="w-full py-2 text-sm font-semibold text-white transition-all duration-300 rounded-lg shadow-md bg-orange-500 hover:bg-orange-600 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add to Cart
          </motion.button>
        )}
      </div>

      <AnimatePresence>
        {animating && (
          <motion.div
            initial={{ scale: 0, opacity: 0.5 }}
            animate={{ scale: 1.6, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 pointer-events-none bg-red-400/20 rounded-xl"
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
